import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Row,
    Col,
    Label,
    Spinner,
    Alert,
    Card,
    UncontrolledTooltip
} from "reactstrap";
import SupplierProductService from "src/modules/3rd-party-management/apis/common/SupplierProductService";
import {
    useCallback,
    useMemo,
    useState
} from "react";
import GeoMap from "src/modules/3rd-party-management/components/geo-map";
import CommonService from "src/modules/3rd-party-management/apis/common/CommonService";
import { AvField, AvForm } from "availity-reactstrap-validation";

const GetGeoLocationDataModal = ({
    t,
    show,
    toggle,
    product,
    onSuccess
}) => {
    const [geoLocationData, setGeoLocationData] = useState([]);

    const {
        data: originCountryGeoData,
        isFetching: originCountryGeoDataIsFetching
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-country-geo-data',
            product?.origin_country?.id
        ],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.fetchCountryGeoData(product?.origin_country?.id);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: !!product?.origin_country?.id,
        onError: () => {
            toast(t('Failed to load geolocation data for the selected country.'), {
                type: 'error'
            });
        }
    });

    const handleAreaChange = useCallback(({ action, id, geoJSON }) => {
        switch(action){
            case 'create': 
                setGeoLocationData((currentState) => {
                    return [
                        ...currentState,
                        {
                            id: id,
                            geoJSON: geoJSON
                        }
                    ]
                })
            break;
            
            case 'edit': 
                setGeoLocationData((currentState) => {
                    const foundIndex = currentState.findIndex((item) => item.id === id);

                    const newState = [...currentState];

                    if(foundIndex < 0) {
                        return newState;
                    }

                    newState[foundIndex].geoJSON = geoJSON;

                    return newState;
                })
            break;

            case 'delete': 
                setGeoLocationData((currentState) => {
                    return currentState.filter((item) => item.id !== id)
                })
            break;
            default:
                break;
        }
    }, []);

    const {
        mutate: updateProductMutation,
        isLoading: updateIsInProcess
    } = useMutation({
        mutationFn: async ({
            id,
            payload
        }) => {
            const service = SupplierProductService.getInstance();
            return await service.updateGeoLocation(id, payload);
        },
        onSuccess: () => {
            onSuccess();
            toggle();

            toast(t("Geolocation for this product has been saved successfully."), {
                type: "success"
            });
        },
        onError: () => {
            toast(t("Geolocation for this product could not be saved. Please try again."), {
                type: "error"
            });
        }
    });
    
    const {
        mutateAsync: validatePlotMutation
    } = useMutation({
        mutationFn: async (payload) => {
            const service = SupplierProductService.getInstance();
            return await service.validateGeoLocation(payload);
        }
    });

    const handleMapLoad = useCallback((loadedItems) => {
        setGeoLocationData(loadedItems);
    }, [
        setGeoLocationData
    ]);

    const plotValidator = useCallback(({ geoJSON }) => {
        return validatePlotMutation({
            ...geoJSON,
            properties: {
                commodity: (product?.commodity || '').toLowerCase(),
                name: `company-test-${product?.commodity}`,
                production_end: (product?.import_date || '').split(' ')[0]
            }
        });
    }, [ validatePlotMutation, product ]);

    const boundaryRestrictionSettings = useMemo(() => {
        return {
            enabled: !!originCountryGeoData?.geojson,
            area: originCountryGeoData?.geojson,
            errorMessage: {
                title: t('Outside Country Boundary'),
                body: (
                    <>
                        {t('The selected area is outside the boundary of the chosen country.')}
                        <br />
                        {t('Please draw your plot within the country limits to ensure valid geolocation data.')}
                    </>
                )
            }
        }
    }, [t, originCountryGeoData?.geojson ]);

    const plotValidationSettings = useMemo(() => {
        return {
            enabled: true,
            validate: plotValidator,
            errorMessage: {
                title: t('Invalid Plot Detected'),
                body: (
                    <>
                        {t('The selected plot did not pass the plausibility verification check.')}
                        <br />
                        {t('This means the plot’s boundaries or geolocation data are not consistent with the expected agricultural area or satellite validation criteria.')}
                        <br />
                        {t('Please review the drawn area and ensure it accurately represents a valid production plot before proceeding.')}
                    </>
                )
            }
        }
    }, [ plotValidator, t ]);

    const handleSubmit = (_e, values) => {
        updateProductMutation({
            id: product.id,
            payload: {
                coordinates: values.coordinates?.map((l) => {
                    return l.geoJSON
                })
            }
        })
    }

    return (
        <Modal 
            size="xl" 
            isOpen={show}
            role="dialog"
            aria-labelledby="addGeoLocationTitle"
            aria-describedby="addGeoLocationDesc"
            aria-modal="true"
        >
            <AvForm
                className="needs-validation m-2"
                onValidSubmit={handleSubmit}
            >
                <ModalHeader
                    className="border-0"
                    toggle={toggle}
                    id="addGeoLocationTitle"
                >
                    {t('Provide EUDR Geolocation Data')}
                </ModalHeader>

                <ModalBody>
                    <Card className="mb-3" body>
                        <h6 className="fw-bold mb-2">
                            {t('Product Information')}
                        </h6>
                        <p className="mb-1">
                            <strong>{t('NACE Code')}:</strong>
                            {` ${product?.name?.class} - ${t(product?.name?.name)}`}
                        </p>
                        <p className="mb-1">
                            <strong>{t('Product Name')}:</strong>
                            {' '}{product?.product_name}
                        </p>
                        <p className="mb-1">
                            <strong>{t('Lot Number')}:</strong>
                            {` ${product?.lot_number}`}
                            <i
                                id="lotInfo"
                                className="ri-information-line ms-1 text-muted"
                                style={{ cursor: "pointer" }}
                                aria-hidden="true"
                            ></i>
                            <UncontrolledTooltip
                                placement="top"
                                target="lotInfo"
                                trigger="hover focus"
                            >
                                {t('A unique identifier assigned to a specific batch of products.')}
                            </UncontrolledTooltip>
                        </p>
                        <p className="mb-1">
                            <strong>{t('Country of Origin')}:</strong>
                            {` ${product?.origin_country?.name}`}
                        </p>
                        <p className="mb-0">
                            <strong>{t('HS Code')}:</strong>
                            {` ${product?.hs_code}`}
                            <i
                                id="hsInfo"
                                className="ri-information-line ms-1 text-muted"
                                style={{ cursor: "pointer" }}
                                aria-hidden="true"
                            ></i>
                            <UncontrolledTooltip
                                placement="top"
                                target="hsInfo"
                                trigger="hover focus"
                            >
                                {t('Harmonized System code used for international product classification.')}
                            </UncontrolledTooltip>
                        </p>
                    </Card>

                    <Alert color="info" id="addGeoLocationDesc">
                        <p className="mb-0">
                            {t('Use the tools below to draw the polygon of the land where this product originated. Ensure the shape fully covers the production plot.')}
                        </p>
                    </Alert>

                    <Row>
                        <Col sm='12' className="mb-3">
                            <Label>
                                {t('Geolocation (Draw Polygon)')}
                            </Label>

                            <GeoMap
                                onAreaChange={handleAreaChange}
                                preDefinedGeo={product?.geolocation_reference}
                                onLoaded={handleMapLoad}
                                multiDraw={true}
                                defaultSearchParam={product?.origin_country?.name}
                                boundaryRestriction={boundaryRestrictionSettings}
                                isLoading={originCountryGeoDataIsFetching}
                                plotValidation={plotValidationSettings}
                            />

                            <AvField
                                name="coordinates"
                                type="hidden"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: true }
                                }}
                                value={geoLocationData}
                            />

                        </Col>
                    </Row>
                </ModalBody>

                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={toggle}
                    >
                        {t('Cancel')}
                    </Button>

                    <Button
                        type="submit"
                        color="primary"
                        disabled={updateIsInProcess}
                    >
                        {updateIsInProcess && (
                            <Spinner
                                size='sm'
                                color='white'
                                className="me-1"
                            />
                        )}

                        {t('Save Geolocation')}
                    </Button>
                </ModalFooter>
            </AvForm>
        </Modal>
    )
};

export default withNamespaces()(GetGeoLocationDataModal);